import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-details-list-view-item.st.css';
import {
    ClampedText,
    cssEllipsis,
    flexColumn,
    Icon,
    RatioMaintainerIgnoreClassName,
    reactCreateMemo,
    RichTextContent
} from '@wix/devzai-utils-react';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    arrayFirst,
    asArray,
    dateResolveFromDateTimeValue,
    evaluateFunction,
    SerializedDateTimeValueAsString,
    Values
} from '@wix/devzai-utils-common';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {
    DoppeActionLayout,
    doppeActionLayoutCanHideDescription,
    doppeActionLayoutCanHideTitle,
    doppeActionLayoutSupportsDescription,
    DoppeListActionSettings
} from '../../../../client-server-common';
import {DoppePageMediaView} from '../../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    doppeViewerIntlDateTimeFormatShortMonthAndDay
} from '../../../../client-server-common/doppe-viewer/doppe-viewer-intl-utils/doppe-viewer-intl-utils';
import {
    DoppeRichTextContentType
} from '../../../../client-server-common/doppe-tiptap-extensions/doppe-tiptap-extensions';
import {WidgetRenderingContextType} from '../../../../client-server-common/doppe-action-types/doppe-action-type';
import {IDoppeViewerActionContext} from '../../../../doppe-sdk';
import {
    DoppeViewerRichTextView,
    DoppeViewerRichTextViewHtmlRenderingMode
} from '../../../../components/doppe-viewer-rich-text-view/doppe-viewer-rich-text-view';

import {
    useDoppeViewerLang
} from '../../../../components/doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import {
    useAssertWidgetRenderingContextType
} from '../../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    DoppeActionTypeBaseListViewItem
} from '../doppe-action-type-base-list-view-item/doppe-action-type-base-list-view-item';

const WidgetRenderingContextTypeCssState = {
    InPage: 'inPage',
    Modal: 'modal',
}

export const DoppeActionTypeDetailsListViewItemContentAlignment = {
    Start: 'start',
    Center: 'center',
} as const;

export type DoppeActionTypeDetailsListViewItemContentAlignment = Values<typeof DoppeActionTypeDetailsListViewItemContentAlignment>;

export const DoppeActionTypeDetailsListViewItem = reactCreateMemo(function DoppeActionTypeListViewItem<PROPS extends DoppeListActionSettings.WithItemClickBehaviour>(
    props: {
        title: string;
        description?: RichTextContent;
        image: WixMediaResource[] | WixMediaResource | null;
        price?: React.ReactNode;
        contentChildren?: React.ReactNode;
        date?: SerializedDateTimeValueAsString;
        textOnImage?: string;
        layout: DoppeActionLayout;
        gridImageRatio?: number;
        centeredIcon?: Icon.Src;
        isOneItem: boolean;
        ribbon?: string;
        hideTitleWhenPossible?: boolean;
        hideDescriptionWhenPossible?: boolean;
        actionContext?: IDoppeViewerActionContext<PROPS>;
        innerItemIndex: number;
        innerItemId: string;
        alignment?: DoppeActionTypeDetailsListViewItemContentAlignment;
    }
) {
    const {
        image,
        price,
        date,
        textOnImage,
        title,
        description,
        layout,
        gridImageRatio,
        centeredIcon,
        ribbon,
        isOneItem = false,
        hideTitleWhenPossible = false,
        hideDescriptionWhenPossible = false,
        actionContext,
        innerItemIndex,
        innerItemId,
        alignment = (
            layout === DoppeActionLayout.Carousel ? DoppeActionTypeDetailsListViewItemContentAlignment.Start : DoppeActionTypeDetailsListViewItemContentAlignment.Center
        ),
        contentChildren
    } = props;

    const doppeViewerLang = useDoppeViewerLang();
    const calculatedImage = useMemo(() => arrayFirst(asArray(image)) ?? null, [image]);

    const widgetRenderingContextType = useAssertWidgetRenderingContextType();

    const mediaViewLayoutSpec = useMemo(() => {
        switch (layout) {
            case DoppeActionLayout.LargeColumn: {
                return {widthHeightRatio: gridImageRatio ?? 1.5};
            }
            case DoppeActionLayout.Swiper: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
            case DoppeActionLayout.Carousel: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
            case DoppeActionLayout.CarouselSmall: {
                return {widthHeightRatio: gridImageRatio ?? 1.2};
            }
            default: {
                return {widthHeightRatio: 1};
            }
        }
    }, [layout, gridImageRatio]);

    const mediaViewWidthSpec = useMemo(() => {
        switch (layout) {
            case DoppeActionLayout.LargeColumn: {
                return {contentRelativeWidth: 0.8}
            }
            case DoppeActionLayout.Swiper: {
                return {contentRelativeWidth: 0.8}
            }
            case DoppeActionLayout.Carousel: {
                return {contentRelativeWidth: 0.7}
            }
            case DoppeActionLayout.CarouselSmall: {
                return {contentRelativeWidth: 0.35}
            }
            default: {
                return {contentRelativeWidth: 1}
            }
        }
    }, [layout])

    return (
        <DoppeActionTypeBaseListViewItem
            className={style(classes.root, {
                alignment: alignment,
                layout: layout,
                isOneItem: isOneItem,
                hasImage: !!calculatedImage,
                widgetRenderingContextType: evaluateFunction(() => {
                    switch (widgetRenderingContextType) {
                        case WidgetRenderingContextType.Modal: {
                            return WidgetRenderingContextTypeCssState.Modal;
                        }
                        case WidgetRenderingContextType.Card: {
                            return WidgetRenderingContextTypeCssState.InPage;
                        }
                        case WidgetRenderingContextType.Strip: {
                            return WidgetRenderingContextTypeCssState.InPage;
                        }
                    }
                }),
            }, flexColumn())}
            actionContext={actionContext}
            innerItemIndex={innerItemIndex}
            innerItemId={innerItemId}
            contentClassName={style(classes.actionTypeListViewItemViewRootContent, flexColumn())}
        >
            <div
                className={style(classes.linkImageContainer)}
            >
                <DoppePageMediaView
                    mediaResource={calculatedImage}
                    layoutSpec={mediaViewLayoutSpec}
                    widthSpec={mediaViewWidthSpec}
                />
                {
                    price ?
                        <div
                            className={style(classes.priceOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis())}
                        >
                            {price}
                        </div>
                        :
                        null
                }
                {
                    textOnImage ?
                        <div
                            className={style(classes.textOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                        >
                            <div
                                className={style(classes.textOnImageText, {}, cssEllipsis(), FlexLayout.fillRemainingHorizontalSpace)}
                            >
                                {textOnImage}
                            </div>

                        </div>
                        :
                        date  ?
                            <div
                                className={style(classes.dateOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                            >
                                <div
                                    className={style(classes.dateText, {}, FlexLayout.fillRemainingHorizontalSpace)}
                                >
                                    {doppeViewerIntlDateTimeFormatShortMonthAndDay(
                                        doppeViewerLang,
                                        dateResolveFromDateTimeValue(date)
                                    )}
                                </div>

                            </div>
                            :
                            null
                }
                {
                    centeredIcon ?
                        <Icon
                            className={style(classes.ceneteredIcon, {}, RatioMaintainerIgnoreClassName)}
                            icon={centeredIcon}
                        />
                        :
                        null
                }
                {
                    ribbon ?
                        <div
                            className={style(classes.ribbonContainer, {dockedToTop: layout === DoppeActionLayout.ImagesGrid}, RatioMaintainerIgnoreClassName, FlexLayout.column.alignToStart)}
                        >
                            <div
                                className={style(classes.ribbon, {}, CommonClasses.ellipsis)}
                            >
                                {ribbon}
                            </div>
                        </div>
                        :
                        null
                }
            </div>
            <div
                className={style(classes.itemContent)}
            >
                {hideTitleWhenPossible && doppeActionLayoutCanHideTitle(layout) ? null : (
                    <div
                        className={style(classes.itemProductTitle, CommonClasses.lineClamp(2))}
                    >
                        {title}
                    </div>
                )}
                {!doppeActionLayoutSupportsDescription(layout) || (doppeActionLayoutCanHideDescription(layout) && hideDescriptionWhenPossible) || !description ? null : (
                    <>
                        <ClampedText
                            className={style(classes.itemProductDescription, {})}
                            maxLines={2}
                            clampText={true}
                        >
                            {typeof description === 'string' ? description : (
                                <DoppeViewerRichTextView
                                    richText={description}
                                    richTextContentType={DoppeRichTextContentType.DoppeActionContent}
                                    htmlRenderingMode={DoppeViewerRichTextViewHtmlRenderingMode.PlainText}
                                />
                            )}
                        </ClampedText>
                    </>
                )}
                {contentChildren}
            </div>
        </DoppeActionTypeBaseListViewItem>
    );
});


